import { Subject } from 'rxjs';
class Options {
  constructor(url, healthCheckInterval = 10000, inactivityThreshold = 15000, eventHistoryLimit = 50) {
    this.url = url;
    this.healthCheckInterval = healthCheckInterval;
    this.inactivityThreshold = inactivityThreshold;
    this.eventHistoryLimit = eventHistoryLimit;
  }
  static create(url, settings = {}) {
    var options = new Options(url);
    Object.keys(settings).forEach(key => {
      if (key in options) {
        options[key] = settings[key];
      }
    });
    return options;
  }
}
class ConnectRequest {
  constructor(params) {
    this.method = "connect";
    this.params = params;
  }
}
class ConnectResponse {
  constructor() {
    this.method = "connect";
  }
}
class Ping {
  constructor() {
    this.method = "ping";
    this.params = {
      timestamp: Date.now()
    };
  }
}
class AcknowledgeRequest {
  constructor(eventId) {
    this.method = "acknowledge";
    this.params = {
      eventId: eventId
    };
  }
}
class SubscribeRequest {
  constructor(tags) {
    this.method = "subscribe";
    this.params = {
      tags: tags
    };
  }
}
class UnsubscribeRequest {
  constructor(tags) {
    this.method = "unsubscribe";
    this.params = {
      tags: tags
    };
  }
}
class NotifyMessage {
  constructor(params) {
    this.method = "notify";
    this.params = params;
  }
}
class EventHistory {
  constructor(limit) {
    this.limit = limit;
    this.events = new Set();
  }
  push(eventId) {
    let exists = this.events.has(eventId);
    if (!exists) {
      this.events.add(eventId);
    }
    if (this.events.size > this.limit) {
      this.events.delete(this.events.values().next().value);
    }
    return !exists;
  }
}
class LogEntry {
  constructor(useCase, fields) {
    this.timestamp = Date.now();
    this.useCase = useCase;
    this.fields = fields ? JSON.parse(JSON.stringify(fields)) : {};
  }
  toString() {
    return `[${new Date(this.timestamp).toUTCString()}][${this.useCase}] ${JSON.stringify(this.fields)}`;
  }
}
function isSLogService(logService) {
  return 'logEvent' in logService;
}
function isLogService(logService) {
  return 'debug' in logService && 'info' in logService && 'warn' in logService && 'error' in logService;
}
var ConnectionState = /*#__PURE__*/function (ConnectionState) {
  ConnectionState[ConnectionState["DISCONNECTED"] = 0] = "DISCONNECTED";
  ConnectionState[ConnectionState["CONNECTING"] = 1] = "CONNECTING";
  ConnectionState[ConnectionState["CONNECTED"] = 2] = "CONNECTED";
  ConnectionState[ConnectionState["HANDSHOOK"] = 3] = "HANDSHOOK";
  ConnectionState[ConnectionState["LOGOUT"] = 4] = "LOGOUT";
  return ConnectionState;
}(ConnectionState || {});
var CloseCode = /*#__PURE__*/function (CloseCode) {
  CloseCode[CloseCode["CLIENT_INACTIVE"] = 4000] = "CLIENT_INACTIVE";
  CloseCode[CloseCode["MALFORMED_MSG"] = 4001] = "MALFORMED_MSG";
  CloseCode[CloseCode["AUTH_FAILURE"] = 4003] = "AUTH_FAILURE";
  CloseCode[CloseCode["SERVER_LOGOUT"] = 4004] = "SERVER_LOGOUT";
  CloseCode[CloseCode["CLIENT_LOGOUT"] = 4500] = "CLIENT_LOGOUT";
  return CloseCode;
}(CloseCode || {});
var LogUseCase = /*#__PURE__*/function (LogUseCase) {
  LogUseCase["CONN_INIT"] = "RTMS_CONN_INIT";
  LogUseCase["CONN_OPEN"] = "RTMS_CONN_OPEN";
  LogUseCase["CONN_HS_INIT"] = "RTMS_CONN_HS_INIT";
  LogUseCase["CONN_HS_DONE"] = "RTMS_CONN_HS_DONE";
  LogUseCase["CONN_ERROR"] = "RTMS_CONN_ERROR";
  LogUseCase["CONN_CLOSE"] = "RTMS_CONN_CLOSE";
  LogUseCase["CONN_SCH_RESTART"] = "RTMS_CONN_SCH_RESTART";
  LogUseCase["CONN_RESTART"] = "RTMS_CONN_RESTART";
  LogUseCase["CONN_INACTIVE"] = "RTMS_CONN_INACTIVE";
  LogUseCase["MSG_RECV_NOTIFY"] = "RTMS_MSG_RECV_NOTIFY";
  LogUseCase["MSG_RECV_UNKNOWN"] = "RTMS_MSG_RECV_UNKNOWN";
  LogUseCase["MSG_SEND_ACK"] = "RTMS_MSG_SEND_ACK";
  LogUseCase["MSG_SEND_PING"] = "RTMS_SEND_PING";
  LogUseCase["MSG_RECV_PING"] = "RTMS_RECV_PING";
  LogUseCase["TAG_SUB"] = "RTMS_TAG_SUB";
  LogUseCase["TAG_UNSUB"] = "RTMS_TAG_UNSUB";
  return LogUseCase;
}(LogUseCase || {});
class Service {
  constructor(options, configService, logService, socketService, timerService) {
    this.lastReceivedMessageTime = 0;
    this.reconnectCount = 0;
    this.connectionState = ConnectionState.DISCONNECTED;
    this.initialized = false;
    this.authenticated = false;
    this.options = options;
    this.configService = configService;
    this.logService = logService;
    this.socketService = socketService;
    this.timerService = timerService;
    this.history = new EventHistory(options.eventHistoryLimit);
    this.tags = new Set();
    this.messageEvents = new Subject();
  }
  get messages() {
    if (!this.initialized) {
      this.connect();
    }
    return this.messageEvents;
  }
  get state() {
    return this.connectionState;
  }
  connect() {
    this.initialized = true;
    switch (this.connectionState) {
      case ConnectionState.DISCONNECTED:
        this.openSocket();
        break;
      case ConnectionState.HANDSHOOK:
        if (!this.authenticated) {
          this.sendConnectMessage();
        }
        break;
      case ConnectionState.LOGOUT:
        this.reconnect();
        break;
    }
  }
  subscribe(tags) {
    if (typeof tags === 'string') {
      tags = [tags];
    }
    let toSubscribe = new Set();
    tags.forEach(tag => {
      if (!this.tags.has(tag)) {
        toSubscribe.add(tag);
        this.tags.add(tag);
      }
    });
    this.logService.logEvent(new LogEntry(LogUseCase.TAG_SUB, {
      tags: Array.from(toSubscribe)
    }));
    if (this.connectionState == ConnectionState.HANDSHOOK) {
      this.sendToSocket(new SubscribeRequest(Array.from(toSubscribe)));
    }
  }
  unsubscribe(tags) {
    if (typeof tags === 'string') {
      tags = [tags];
    }
    let toUnsubscribe = new Set();
    tags.forEach(tag => {
      if (this.tags.has(tag)) {
        toUnsubscribe.add(tag);
        this.tags.delete(tag);
      }
    });
    this.logService.logEvent(new LogEntry(LogUseCase.TAG_UNSUB, {
      tags: Array.from(toUnsubscribe)
    }));
    if (this.connectionState = ConnectionState.HANDSHOOK) {
      this.sendToSocket(new UnsubscribeRequest(Array.from(toUnsubscribe)));
    }
  }
  close() {
    this.sock.close(4500, "Client initiated close");
  }
  openSocket() {
    this.logService.logEvent(new LogEntry(LogUseCase.CONN_INIT, {
      url: this.options.url
    }));
    this.connectionState = ConnectionState.CONNECTING;
    this.sock = this.socketService.create(this.options.url);
    this.sock.onopen = () => this.onOpen();
    this.sock.onmessage = e => this.onMessage(e);
    this.sock.onerror = e => this.onError(e);
    this.sock.onclose = e => this.onClose(e);
  }
  onOpen() {
    this.logService.logEvent(new LogEntry(LogUseCase.CONN_OPEN, {
      url: this.options.url
    }));
    this.connectionState = ConnectionState.CONNECTED;
    this.sendConnectMessage();
  }
  sendConnectMessage() {
    let params = this.configService.params();
    this.logService.logEvent(new LogEntry(LogUseCase.CONN_HS_INIT, {
      params: params
    }));
    this.sendToSocket(new ConnectRequest(params));
  }
  onMessage(e) {
    this.lastReceivedMessageTime = Date.now();
    const msg = JSON.parse(e.data || 'null');
    switch (msg && msg['method']) {
      case 'connect':
        let response = msg;
        this.reconnectCount = 0;
        this.connectionState = ConnectionState.HANDSHOOK;
        this.authenticated = !!response.params.auth;
        this.logService.logEvent(new LogEntry(LogUseCase.CONN_HS_DONE, {
          sessionId: response.params.session,
          authenticated: this.authenticated
        }));
        this.startHealthCheckTimer();
        // Subscribe to tags
        if (this.tags.size > 0) {
          this.sendToSocket(new SubscribeRequest(Array.from(this.tags)));
        }
        break;
      case 'notify':
        let notification = msg;
        this.logService.logEvent(new LogEntry(LogUseCase.MSG_RECV_NOTIFY, {
          params: notification.params
        }));
        let isUniqueEvent = this.history.push(notification.params.eventId);
        if (notification.params.privileged) {
          this.sendToSocket(new AcknowledgeRequest(notification.params.eventId));
          this.logService.logEvent(new LogEntry(LogUseCase.MSG_SEND_ACK, {
            eventId: notification.params.eventId
          }));
        }
        if (isUniqueEvent) {
          this.messageEvents.next(msg.params);
        }
        break;
      case 'ping':
        const latency = Date.now() - msg.params.timestamp;
        this.logService.logEvent(new LogEntry(LogUseCase.MSG_RECV_PING, {
          latency: latency
        }));
        break;
      default:
        this.logService.logEvent(new LogEntry(LogUseCase.MSG_RECV_UNKNOWN, {
          msg: msg
        }));
        break;
    }
  }
  sendToSocket(msg) {
    this.sock.send(JSON.stringify(msg));
  }
  onError(e) {
    this.logService.logEvent(new LogEntry(LogUseCase.CONN_ERROR));
    this.connectionState = ConnectionState.DISCONNECTED;
    this.authenticated = false;
  }
  onClose(e) {
    // 1xxx - WebSocket internal error codes
    // 4000 - Timeout waiting for Connect.
    // 4001 - Invalid Message received from client
    // 4003 - Authentication failure
    // 4004 - Login on another connection
    let reasonType = "WS_INTERNAL";
    switch (e.code) {
      case 4000:
      case 4001:
      case 4003:
      case 4004:
      case 4500:
        reasonType = CloseCode[e.code];
        break;
      default:
        if (e.code < 4000) {
          reasonType = "WS_INTERNAL";
        } else {
          reasonType = "APP_ERR_UNKNOWN";
        }
    }
    this.logService.logEvent(new LogEntry(LogUseCase.CONN_CLOSE, {
      code: e.code,
      type: reasonType,
      reason: e.reason
    }));
    this.connectionState = ConnectionState.DISCONNECTED;
    this.authenticated = false;
    this.stopHealthCheckTimer();
    if (e.code <= 4000) {
      let timeout = this.reconnectCount > 10 ? 5000 : 1000;
      this.logService.logEvent(new LogEntry(LogUseCase.CONN_SCH_RESTART, {
        duration: timeout
      }));
      this.timerService.setTimeout(() => this.reconnect(), timeout);
    }
  }
  reconnect() {
    this.logService.logEvent(new LogEntry(LogUseCase.CONN_RESTART, {
      attempt: this.reconnectCount + 1
    }));
    this.reconnectCount++;
    this.openSocket();
  }
  startHealthCheckTimer() {
    this.stopHealthCheckTimer();
    if (this.options.healthCheckInterval > 0) {
      // So that we don't get confusing delays with long configured interval
      const frequency = Math.min(1000, this.options.healthCheckInterval);
      this.healthCheckIntervalId = this.timerService.setInterval(() => this.onHealthCheck(), frequency);
    }
  }
  stopHealthCheckTimer() {
    if (this.healthCheckIntervalId) {
      this.timerService.clearInterval(this.healthCheckIntervalId);
      this.healthCheckIntervalId = null;
    }
  }
  onHealthCheck() {
    const elapsed = Math.round((Date.now() - this.lastReceivedMessageTime) / 1000) * 1000;
    if (elapsed >= this.options.inactivityThreshold) {
      this.logService.logEvent(new LogEntry(LogUseCase.CONN_INACTIVE, {
        idleTime: elapsed
      }));
      this.sock.close();
    } else if (elapsed >= this.options.healthCheckInterval) {
      this.logService.logEvent(new LogEntry(LogUseCase.MSG_SEND_PING, {
        idleTime: elapsed
      }));
      this.sendToSocket(new Ping());
    }
  }
}
const URL_SPEC_WS = 'ws';
const URL_SPEC_HTTP = 'http';
function WSURL(url) {
  if (url.slice(0, URL_SPEC_HTTP.length) === URL_SPEC_HTTP) {
    url = URL_SPEC_WS + url.slice(URL_SPEC_HTTP.length);
    url = url + (url[url.length - 1] === '/' ? 'websocket' : '/websocket');
  }
  return url;
}
class DefaultWSService {
  create(url) {
    if (typeof WebSocket !== 'undefined') {
      return new WebSocket(WSURL(url));
    } else {
      throw new Error("WebSocket support missing");
    }
  }
}
class DefaultTimerService {
  setTimeout(operation, timeout) {
    return setTimeout(operation, timeout);
  }
  setInterval(operation, timeout) {
    return setInterval(operation, timeout);
  }
  clearTimeout(id) {
    clearTimeout(id);
  }
  clearInterval(id) {
    clearInterval(id);
  }
}
class DefaultConsoleLogService {
  constructor(level = "info") {
    this.order = {
      "debug": 0,
      "info": 1,
      "warn": 2,
      "error": 3
    };
    this.logLevel = level;
  }
  debug(message, ...optionalParams) {
    this.emitLog("debug", message, ...optionalParams);
  }
  info(message) {
    this.emitLog("info", message);
  }
  warn(message, ...optionalParams) {
    this.emitLog("warn", message, ...optionalParams);
  }
  error(message, ...optionalParams) {
    this.emitLog("error", message, ...optionalParams);
  }
  emitLog(level, message, ...optionalParams) {
    if (this.order[level] >= this.order[this.logLevel]) {
      console[level](message, ...optionalParams);
    }
  }
}
class DefaultSLogService {
  constructor(logService) {
    this.logService = logService;
  }
  logEvent(e) {
    switch (e.useCase) {
      case LogUseCase.CONN_INIT:
        this.logService.info(`[${e.useCase}] Opening connection to ${e.fields.url}`);
        break;
      case LogUseCase.CONN_OPEN:
        this.logService.debug(`[${e.useCase}] Connection opened to ${e.fields.url}`);
        break;
      case LogUseCase.CONN_HS_INIT:
        this.logService.debug(`[${e.useCase}] Sending handshake with params : ${JSON.stringify(e.fields.params)}`);
        break;
      case LogUseCase.CONN_HS_DONE:
        this.logService.info(`[${e.useCase}] Connected to RTMS with sessionId = "${e.fields.sessionId}", authenticated = ${e.fields.authenticated}.`);
        break;
      case LogUseCase.CONN_ERROR:
        this.logService.warn(`[${e.useCase}] Connection error`);
        break;
      case LogUseCase.CONN_CLOSE:
        this.logService.warn(`[${e.useCase}] Connection closed. CloseType = ${e.fields.type}, Code = ${e.fields.code}, Reason = "${e.fields.reason}".`);
        break;
      case LogUseCase.CONN_SCH_RESTART:
        this.logService.info(`[${e.useCase}] Scheduling reconnect in ${e.fields.duration} ms.`);
        break;
      case LogUseCase.CONN_INACTIVE:
        this.logService.warn(`[${e.useCase}] Connection idle for ${e.fields.idleTime} ms. Resetting connection`);
        break;
      case LogUseCase.CONN_RESTART:
        this.logService.warn(`[${e.useCase}] Attempting to reconnect... ${e.fields.attempt}.`);
        break;
      case LogUseCase.MSG_RECV_NOTIFY:
        this.logService.debug(`[${e.useCase}] Received notification with eventId = ${e.fields.params.eventId}, type = ${e.fields.params.type}, privileged = ${e.fields.params.type}, payload = ${JSON.stringify(e.fields.params.payload)}.`);
        break;
      case LogUseCase.MSG_SEND_ACK:
        this.logService.debug(`[${e.useCase}] Send Ack for eventId = ${e.fields.eventId}`);
        break;
      case LogUseCase.MSG_RECV_UNKNOWN:
        this.logService.warn(`[${e.useCase}] Received unknown message : ${JSON.stringify(e.fields.msg)}.`);
        break;
      case LogUseCase.MSG_SEND_PING:
        this.logService.debug(`[${e.useCase}] Connection idle for ${e.fields.idleTime} ms, sending ping`);
        break;
      case LogUseCase.MSG_RECV_PING:
        this.logService.debug(`[${e.useCase}] Received ping response with latency ${e.fields.latency} ms`);
        break;
      case LogUseCase.TAG_SUB:
        this.logService.info(`[${e.useCase}] Subscribed tags : ${JSON.stringify(e.fields.tags)}`);
        break;
      case LogUseCase.TAG_UNSUB:
        this.logService.info(`[${e.useCase}] Unsubscribed tags : ${JSON.stringify(e.fields.tags)}`);
        break;
      default:
        this.logService.warn(`Usecase = ${e.useCase}, fields =${JSON.stringify(e.fields)}`);
        break;
    }
  }
}
const DEFAULT_TIMER_SERVICE = new DefaultTimerService();
const DEFAULT_SOCKET_SERVICE = new DefaultWSService();
const DEFAULT_CONSOLE_LOG_SERVICE = new DefaultConsoleLogService("info");
const DEFAULT_CONSOLE_DBG_LOG_SERVICE = new DefaultConsoleLogService("debug");
const DEFAULT_SLOG_SERVICE = new DefaultSLogService(DEFAULT_CONSOLE_LOG_SERVICE);
function create(options, configService, logService = DEFAULT_SLOG_SERVICE, socketService = DEFAULT_SOCKET_SERVICE, timerService = DEFAULT_TIMER_SERVICE) {
  let logSvc = DEFAULT_SLOG_SERVICE;
  if (isLogService(logService)) {
    logSvc = new DefaultSLogService(logService);
  } else if (isSLogService(logService)) {
    logSvc = logService;
  }
  return new Service(options, configService, logSvc, socketService, timerService);
}
function createInstance(url, configService, ...options) {
  let ctx = {
    options: new Options(url),
    logService: DEFAULT_SLOG_SERVICE,
    socketService: DEFAULT_SOCKET_SERVICE,
    timerService: DEFAULT_TIMER_SERVICE
  };
  for (const option of options) {
    option(ctx);
  }
  return new Service(ctx.options, configService, ctx.logService, ctx.socketService, ctx.timerService);
}
function WithLogService(logService) {
  return ctx => {
    ctx.logService = new DefaultSLogService(logService);
  };
}
function WithSLogService(logService) {
  return ctx => {
    ctx.logService = logService;
  };
}
function WithSocketService(socketService) {
  return ctx => {
    ctx.socketService = socketService;
  };
}
function WithTimerService(timerService) {
  return ctx => {
    ctx.timerService = timerService;
  };
}
function WithSettings(settings) {
  return ctx => {
    Object.keys(settings).forEach(key => {
      if (key in ctx.options) {
        ctx.options[key] = settings[key];
      }
    });
  };
}

/*
 * Public API Surface of client
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CloseCode, ConnectionState, DEFAULT_CONSOLE_DBG_LOG_SERVICE, DEFAULT_CONSOLE_LOG_SERVICE, DEFAULT_SOCKET_SERVICE, DEFAULT_TIMER_SERVICE, DefaultConsoleLogService, DefaultTimerService, DefaultWSService, LogEntry, LogUseCase, Options, Service, WSURL, WithLogService, WithSLogService, WithSettings, WithSocketService, WithTimerService, create, createInstance, isLogService, isSLogService };
